/**
 * worlds — the six playable WORLDS and the planets in each.
 *
 * A "world" is a city: Hong Kong (the `/planet` + `/hk` route) plus the five global
 * cities that carry a `region` tag. HK is deliberately NOT a RegionId — that type means
 * "a non-HK city whose districts are tagged `region:`", and pickerGroupOf short-circuits
 * on `d.region`, so widening it would empty the HK picker. A file-local WorldId union
 * gives the UI the symmetry it needs with a blast radius of one module.
 *
 * HK keeps its six-group scale taxonomy (globes → territories → downtown → areas →
 * cube-belts → ultra); the city rosters are flat. Both shapes render through the same
 * grouped list so one browser serves all six worlds.
 */
import {
  PICKER_TIERS,
  tierDistricts,
  tierSections,
  districtsForRegion,
  planetKindOf,
  REGIONS,
  GAME_PLANETS,
  GAMES_WORLD,
  type HdDistrict,
  type RegionId,
} from './districts.ts';
import { districtHaystack, worldPlaceAliases } from './placeLocale.ts';

/**
 * Hong Kong, every global-city region, and the two worlds that are not cities.
 *
 * `games` is deliberately NOT a RegionId — see GAMES_WORLD in districts.ts for why the
 * city rules (ten planets, every one a cube-belt) are right for cities and wrong for a
 * collection of computed worlds.
 */
export type WorldId = 'hk' | RegionId | 'games';

/**
 * What KIND of world this is.
 *
 * `city` — a real place on Earth. It has a continent, a lat/lon beacon, a flag, and it
 *   counts towards "N cities across N continents" on the homepage.
 * `collection` — a curated world whose planets are real places but which is NOT itself a
 *   place. `sport` is the first: its belts are in London, Sydney, Beijing, St Andrews…
 *   so asking which continent "Sport" is on, or where to plot its globe beacon, has no
 *   honest answer. The kind is what lets those seams EXCLUDE it instead of inventing one.
 * `games` — a world whose planets are not real places AT ALL. They are computed from a
 *   seed (ADR-0272), so they have no continent, no beacon, and no ODbL notice, because
 *   no map data went into them. Same exclusions as `collection`, different reason: a
 *   collection's planets are somewhere and the world is not; a game world's planets are
 *   nowhere.
 */
export type WorldKind = 'city' | 'collection' | 'games';

/** Worlds that are real places — the only ones with a continent and a map beacon. */
export type CityWorldId = Exclude<WorldId, 'sport' | 'games'>;

export interface WorldSpec {
  readonly id: WorldId;
  /** Tab label / place name ("Hong Kong", "Tokyo"). */
  readonly place: string;
  /** Route this world's planets live on. HK's canonical path stays `/planet` — it is
   *  the published WorldIndexV1 deep-link target (ADR-0228); `/hk` is an alias. */
  readonly path: string;
  readonly flag: string;
  /** Defaults to 'city' — every world was one until the sport collection. */
  readonly kind?: WorldKind;
}

/** A real place, so it may carry a continent, a beacon and a place in the city count. */
export function isCityWorld(id: WorldId): id is CityWorldId {
  return (WORLDS.find((w) => w.id === id)?.kind ?? 'city') === 'city';
}

// Flag policy: Shenzhen and Taipei deliberately carry NO flag emoji. Any choice
// of 🇨🇳/🇹🇼 next to these two names takes a side in the One-China dispute; a
// city picker has no business doing that. Renderers must treat `flag` as
// optional presentation, never required.
export const WORLDS: ReadonlyArray<WorldSpec> = [
  { id: 'hk', place: 'Hong Kong', path: '/planet', flag: '🇭🇰' },
  { id: 'tokyo', place: 'Tokyo', path: '/tokyo', flag: '🇯🇵' },
  { id: 'nyc', place: 'New York', path: '/nyc', flag: '🇺🇸' },
  { id: 'paris', place: 'Paris', path: '/paris', flag: '🇫🇷' },
  { id: 'amsterdam', place: 'Amsterdam', path: '/amsterdam', flag: '🇳🇱' },
  { id: 'rotterdam', place: 'Rotterdam', path: '/rotterdam', flag: '🇳🇱' },
  { id: 'berlin', place: 'Berlin', path: '/berlin', flag: '🇩🇪' },
  { id: 'sanfrancisco', place: 'San Francisco', path: '/sanfrancisco', flag: '🇺🇸' },
  { id: 'vienna', place: 'Vienna', path: '/vienna', flag: '🇦🇹' },
  { id: 'barcelona', place: 'Barcelona', path: '/barcelona', flag: '🇪🇸' },
  { id: 'copenhagen', place: 'Copenhagen', path: '/copenhagen', flag: '🇩🇰' },
  { id: 'toronto', place: 'Toronto', path: '/toronto', flag: '🇨🇦' },
  { id: 'melbourne', place: 'Melbourne', path: '/melbourne', flag: '🇦🇺' },
  { id: 'edinburgh', place: 'Edinburgh', path: '/edinburgh', flag: '🇬🇧' },
  { id: 'seoul', place: 'Seoul', path: '/seoul', flag: '🇰🇷' },
  { id: 'losangeles', place: 'Los Angeles', path: '/losangeles', flag: '🇺🇸' },
  { id: 'dubai', place: 'Dubai', path: '/dubai', flag: '🇦🇪' },
  { id: 'bangkok', place: 'Bangkok', path: '/bangkok', flag: '🇹🇭' },
  { id: 'capetown', place: 'Cape Town', path: '/capetown', flag: '🇿🇦' },
  { id: 'rio', place: 'Rio de Janeiro', path: '/rio', flag: '🇧🇷' },
  { id: 'istanbul', place: 'Istanbul', path: '/istanbul', flag: '🇹🇷' },
  { id: 'venice', place: 'Venice', path: '/venice', flag: '🇮🇹' },
  { id: 'shenzhen', place: 'Shenzhen', path: '/shenzhen', flag: '' },
  { id: 'taipei', place: 'Taipei', path: '/taipei', flag: '' },
  { id: 'miami', place: "Miami", path: '/miami', flag: '🇺🇸' },
  { id: 'fukuoka', place: "Fukuoka", path: '/fukuoka', flag: '🇯🇵' },
  { id: 'nagoya', place: "Nagoya", path: '/nagoya', flag: '🇯🇵' },
  { id: 'yokohama', place: "Yokohama", path: '/yokohama', flag: '🇯🇵' },
  { id: 'osaka', place: "Osaka", path: '/osaka', flag: '🇯🇵' },
  { id: 'athens', place: "Athens", path: '/athens', flag: '🇬🇷' },
  { id: 'lisbon', place: "Lisbon", path: '/lisbon', flag: '🇵🇹' },
  { id: 'helsinki', place: "Helsinki", path: '/helsinki', flag: '🇫🇮' },
  { id: 'zurich', place: "Zurich", path: '/zurich', flag: '🇨🇭' },
  { id: 'prague', place: "Prague", path: '/prague', flag: '🇨🇿' },
  { id: 'kyoto', place: "Kyoto", path: '/kyoto', flag: '🇯🇵' },
  { id: 'vatican', place: "Vatican City", path: '/vatican', flag: '🇻🇦' },
  { id: 'monaco', place: "Monaco", path: '/monaco', flag: '🇲🇨' },
  { id: 'macau', place: "Macau", path: '/macau', flag: '🇲🇴' },
  { id: 'geneva', place: "Geneva", path: '/geneva', flag: '🇨🇭' },
  { id: 'singapore', place: "Singapore", path: '/singapore', flag: '🇸🇬' },
  { id: 'rome', place: "Rome", path: '/rome', flag: '🇮🇹' },
  { id: 'london', place: "London", path: '/london', flag: '🇬🇧' },
  { id: 'jakarta', place: "Jakarta", path: '/jakarta', flag: '🇮🇩' },
  { id: 'mexicocity', place: "Mexico City", path: '/mexicocity', flag: '🇲🇽' },
  { id: 'sanjose', place: "San Jose", path: '/sanjose', flag: '🇺🇸' },
  { id: 'telaviv', place: "Tel Aviv", path: '/telaviv', flag: '🇮🇱' },
  { id: 'chicago', place: "Chicago", path: '/chicago', flag: '🇺🇸' },
  { id: 'boston', place: "Boston", path: '/boston', flag: '🇺🇸' },
  { id: 'mumbai', place: "Mumbai", path: '/mumbai', flag: '🇮🇳' },
  { id: 'moscow', place: "Moscow", path: '/moscow', flag: '🇷🇺' },
  { id: 'nanjing', place: "Nanjing", path: '/nanjing', flag: '' },
  { id: 'beijing', place: "Beijing", path: '/beijing', flag: '' },
  { id: 'shanghai', place: "Shanghai", path: '/shanghai', flag: '' },
  { id: 'sydney', place: "Sydney", path: '/sydney', flag: '🇦🇺' },
  { id: 'kualalumpur', place: "Kuala Lumpur", path: '/kualalumpur', flag: '🇲🇾' },
  { id: 'washingtondc', place: "Washington", path: '/washingtondc', flag: '🇺🇸' },
  { id: 'xian', place: "Xi'an", path: '/xian', flag: '' },
  { id: 'zhengzhou', place: 'Zhengzhou', path: '/zhengzhou', flag: '' },
  { id: 'luoyang', place: 'Luoyang', path: '/luoyang', flag: '' },
  // The first COLLECTION world: real sport districts from all over the planet, gathered
  // under one roof. No flag and no continent — see WorldKind.
  { id: 'sport', place: 'Sport', path: '/sport', flag: '', kind: 'collection' },
  { id: 'games', place: 'Game worlds', path: '/games', flag: '', kind: 'games' },
];

export function getWorld(id: string | null | undefined): WorldSpec | null {
  return WORLDS.find((w) => w.id === id) ?? null;
}

/** Which world a district belongs to — its `region`, or Hong Kong when untagged. */
export function worldOfDistrict(d: HdDistrict): WorldId {
  // `game` first: a computed planet carries no region, and defaulting it to 'hk' would
  // file a generated desert arena in the Hong Kong picker.
  if (d.game) return 'games';
  return d.region ?? 'hk';
}

export interface WorldGroup {
  readonly key: string;
  readonly label: string;
  readonly icon: string;
  readonly accent: boolean;
  readonly members: ReadonlyArray<HdDistrict>;
  /** Labelled shelves inside the group (HK tiers only). A single `label: null` section
   *  means "render flat" — `members` is always the flattened roster either way, so a
   *  caller that does not care about sections keeps working unchanged. */
  readonly sections: ReadonlyArray<{ readonly label: string | null; readonly members: ReadonlyArray<HdDistrict> }>;
}

/**
 * The browsable planets of a world, grouped for display. Hong Kong shelves its roster
 * on the three PICKER_TIERS (belt / special / cube) — the scale GROUPS still classify
 * every planet underneath and still gate Siege, but seven headings (six after the empty
 * `areas` tier is dropped) asked the player to learn the bake taxonomy before choosing a
 * place. A city returns one flat group. Empty tiers are dropped so a young city shows
 * nothing but its own row.
 */
export function groupsForWorld(id: WorldId): WorldGroup[] {
  if (id === 'hk') {
    return PICKER_TIERS.map((t) => ({
      key: t.key,
      label: t.label,
      icon: t.icon,
      accent: t.accent,
      members: tierDistricts(t.key),
      sections: tierSections(t.key),
    })).filter((g) => g.members.length > 0);
  }
  if (id === 'games') {
    // One group, split into sections by HOW the world was made. That distinction is the
    // honest one to surface: a computed world and an imported open-licensed map carry
    // different provenance and different attribution, and a player deserves to see which
    // is which before they walk into it.
    const members = GAME_PLANETS;
    const sectionOf = (d: HdDistrict) =>
      d.game?.source === 'open-map'
        ? 'Open-licensed maps'
        : d.game?.source === 'real-double'
          ? 'Real places in a game mood'
          : 'Computed worlds — generated from a seed, no map data';
    const labels = [...new Set(members.map(sectionOf))];
    return [
      {
        key: 'games',
        label: GAMES_WORLD.label,
        icon: '🎮',
        accent: true,
        members,
        sections: labels.map((label) => ({ label, members: members.filter((d) => sectionOf(d) === label) })),
      },
    ];
  }
  const spec = REGIONS[id];
  // Lead with the whole-city cube. A city's roster is registry-ordered, which buried the
  // one planet that IS the city at position 16 of 16 — last card, bottom row, styled
  // exactly like the fifteen neighbourhood belts around it, with nothing to say "start
  // here". Hong Kong never had this problem because its picker groups by scale, so
  // hksar-cube sits under a "Territories" heading; a city roster is flat and has no such
  // signpost. Ordering is the whole fix: the overview is the natural way into a city, and
  // first-card is unmissable. Sort is stable, so the districts keep registry order.
  const members = [...districtsForRegion(id)].sort(
    (a, b) =>
      Number(planetKindOf(b) === 'territories') - Number(planetKindOf(a) === 'territories'),
  );
  return members.length
    ? [{ key: id, label: spec.label, icon: '', accent: true, members, sections: [{ label: null, members }] }]
    : [];
}

export interface HeaderMenuRow {
  readonly id: string;
  /** Shelf name drawn above this row. Present on the first planet of each Hong Kong shelf. */
  readonly shelf?: string;
}

/**
 * Planets in the top-left dropdown, in the order the list should read.
 *
 * Hong Kong follows the three shelves (HD districts, special, whole-city cubes)
 * and labels each shelf once. Passing the whole-city id must not pull that cube
 * back to the top — a flat hoist would undo the shelf order.
 *
 * A city still leads with its whole-city cube. Its roster is one flat group, so
 * the cube is the only signpost that says "start here".
 */
export function headerMenuRows(id: WorldId, wholeCityId: string | null): HeaderMenuRow[] {
  if (id === 'hk') {
    return groupsForWorld('hk').flatMap((group) =>
      group.members.map((d, index) => ({
        id: d.id,
        shelf: index === 0 ? `${group.icon} ${group.label} · ${group.members.length}` : undefined,
      })),
    );
  }
  const list = groupsForWorld(id).flatMap((group) => group.members);
  const ordered = wholeCityId
    ? list.slice().sort((a, b) => Number(b.id === wholeCityId) - Number(a.id === wholeCityId))
    : list;
  return ordered.map((d) => ({ id: d.id }));
}

/**
 * Worlds whose place name (or id) matches a free-text query — city search.
 *
 * The picker's search only ever filtered the OPEN world's planets, so typing a city
 * name returned "No planet matches" even though that city is one of the tabs: Vienna
 * has 16 planets and exactly ONE of them ("Vienna · Whole-City Cube") carries the word
 * "Vienna" — the rest are Innere Stadt, Karlsplatz, Belvedere. The city is what people
 * search for, and it is not findable from its own planets' names.
 *
 * Matches the id too, so a pasted URL slug ("sanfrancisco") lands as well.
 *
 * An empty query returns nothing rather than every world: the tab strip already shows
 * all of them, and a "matching worlds" block listing 29 entries would be noise.
 */
export function worldsMatching(query: string): WorldSpec[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return [];
  return WORLDS.filter((w) =>
    worldPlaceAliases(w.id, w.place).some((name) => name.toLowerCase().includes(q))
    || w.id.toLowerCase().includes(q),
  );
}

/**
 * Filter a world's groups by a free-text query over planet name and id.
 *
 * Filters `sections` and derives `members` from the result, so the two can never
 * disagree. They did: the picker draws its cards from `g.sections[].members` but reads
 * its heading badge from `g.members.length`, and a filter that rewrote only `members`
 * shrank the badge while leaving every card on screen — search appeared dead. That is
 * the invariant WorldGroup documents ("`members` is always the flattened roster"), so
 * the safe way to filter is to filter the sections and flatten.
 *
 * An empty query returns the input array by identity — no rebuild, no reshaping of the
 * HK tier sections.
 */
export function filterWorldGroups(
  groups: ReadonlyArray<WorldGroup>,
  query: string,
): WorldGroup[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return groups as WorldGroup[];
  const hit = (d: HdDistrict): boolean => districtHaystack(d.name, d.id).includes(q);
  return groups
    .map((g) => {
      const sections = g.sections
        .map((sec) => ({ ...sec, members: sec.members.filter(hit) }))
        .filter((sec) => sec.members.length > 0);
      return { ...g, sections, members: sections.flatMap((sec) => sec.members) };
    })
    .filter((g) => g.members.length > 0);
}

/** How many planets a world offers (what the tab badge counts). */
export function planetCountForWorld(id: WorldId): number {
  return groupsForWorld(id).reduce((n, g) => n + g.members.length, 0);
}
